import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import SearchableDropdown from '../components/SearchableDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faBan, faRightLeft } from '@fortawesome/free-solid-svg-icons';
import { fetchClientById, updateClient, voidClient, convertLeadToClient } from '../api/clients';
import { fetchVerifiedUsers, type VerifiedUser } from '../api/users';
// Generic despite the name: Postgres DATE columns arrive as full ISO strings.
import { formatTaskDate, toDateInputValue } from '../utils/formatTaskDate';
import { products } from '../data/tasks';
import MarketingOptOutBadge from '../components/MarketingOptOutBadge';
import './Page.css';
import './ClientDetail.css';
import './LeadDetail.css';

const LeadDetail: React.FC = () => {
  const navigate = useNavigate();
  const { leadId } = useParams<{ leadId: string }>();
  const leadIdNum = leadId ? parseInt(leadId, 10) : null;

  const [apiLead, setApiLead] = useState<Awaited<ReturnType<typeof fetchClientById>>>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const leadData = apiLead?.type === 'lead' ? apiLead : null;

  // Display state
  const [companyName, setCompanyName] = useState('');
  const [pointOfContact, setPointOfContact] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [source, setSource] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [productInterested, setProductInterested] = useState('');
  const [notes, setNotes] = useState('');
  const [employee, setEmployee] = useState('');
  const [firstContact, setFirstContact] = useState('');

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editCompany, setEditCompany] = useState('');
  const [editPointOfContact, setEditPointOfContact] = useState('');
  const [editContactEmail, setEditContactEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editSource, setEditSource] = useState('');
  const [editProduct, setEditProduct] = useState('');
  const [editEmployee, setEditEmployee] = useState('');
  const [editSelectedUserId, setEditSelectedUserId] = useState<number | null>(null);
  const [editFirstContact, setEditFirstContact] = useState('');
  const [verifiedUsers, setVerifiedUsers] = useState<VerifiedUser[]>([]);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  // Void modal state
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voidReasonInput, setVoidReasonInput] = useState('');
  const [voidSubmitting, setVoidSubmitting] = useState(false);
  const [voidError, setVoidError] = useState('');

  // Convert modal state
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [convertSubmitting, setConvertSubmitting] = useState(false);
  const [convertError, setConvertError] = useState('');

  const isVoid = leadData?.client_status === 'Void';

  // Update message
  const [leadMessage, setLeadMessage] = useState('');

  useEffect(() => {
    if (!leadIdNum) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const c = await fetchClientById(leadIdNum);
        if (cancelled) return;
        if (!c || c.type !== 'lead') {
          setNotFound(true);
          setApiLead(null);
        } else {
          setApiLead(c);
        }
      } catch {
        if (!cancelled) { setNotFound(true); setApiLead(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leadIdNum]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const users = await fetchVerifiedUsers();
        if (!cancelled) setVerifiedUsers(users);
      } catch {
        if (!cancelled) setVerifiedUsers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!leadData) return;
    setCompanyName(leadData.company);
    setPointOfContact(leadData.point_of_contact);
    setContactEmail(leadData.contact_email ?? '');
    setSource(leadData.source ?? '');
    setPhoneNumber(leadData.contact_phone ?? '');
    setProductInterested(leadData.product ?? '');
    setNotes(leadData.notes ?? '');
    setEmployee(leadData.employee_name ?? '');
    setFirstContact(toDateInputValue(leadData.first_contact));
  }, [leadData]);

  // Matching the assigned name to a user id needs the user list, which loads
  // separately, so this runs once both are in.
  useEffect(() => {
    if (!leadData || verifiedUsers.length === 0) return;
    const match = verifiedUsers.find((u) => u.username === (leadData.employee_name ?? ''));
    setEditSelectedUserId(match ? match.id : null);
  }, [leadData, verifiedUsers]);

  const [editNotes, setEditNotes] = useState('');

  const handleEditClick = () => {
    setSaveError('');
    setEditCompany(companyName);
    setEditPointOfContact(pointOfContact);
    setEditContactEmail(contactEmail);
    setEditPhone(phoneNumber);
    setEditSource(source);
    setEditProduct(productInterested);
    setEditEmployee(employee);
    setEditFirstContact(firstContact);
    setEditNotes(notes);
    setIsEditModalOpen(true);
  };

  const handleEditSave = async () => {
    if (!leadIdNum) return;
    setSaveError('');
    setSaving(true);
    try {
      const payload: Parameters<typeof updateClient>[1] = {
        point_of_contact: editPointOfContact.trim(),
        contact_email: editContactEmail.trim() || null,
        contact_phone: editPhone.trim() || null,
        source: editSource.trim() || null,
        product: editProduct.trim() || null,
        first_contact: editFirstContact.trim() || null,
        notes: editNotes.trim() || null,
      };
      // Prefer the linked user so the lead follows a real account; fall back to
      // the typed name, and an empty box clears the assignment.
      if (editSelectedUserId != null) payload.user_id = editSelectedUserId;
      else if (editEmployee.trim()) payload.employee_name = editEmployee.trim();
      else payload.employee_name = null;
      const updated = await updateClient(leadIdNum, payload);
      setApiLead(updated);
      setCompanyName(updated.company);
      setPointOfContact(updated.point_of_contact);
      setContactEmail(updated.contact_email ?? '');
      setPhoneNumber(updated.contact_phone ?? '');
      setSource(updated.source ?? '');
      setProductInterested(updated.product ?? '');
      setNotes(updated.notes ?? '');
      setEditNotes(updated.notes ?? '');
      setEmployee(updated.employee_name ?? '');
      setEditEmployee(updated.employee_name ?? '');
      setFirstContact(toDateInputValue(updated.first_contact));
      setEditFirstContact(toDateInputValue(updated.first_contact));
      setIsEditModalOpen(false);
      setLeadMessage('Lead updated successfully.');
      setTimeout(() => setLeadMessage(''), 4000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to update lead.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCancel = () => {
    setSaveError('');
    setEditNotes(notes);
    setIsEditModalOpen(false);
  };

  const openVoidModal = () => {
    setVoidReasonInput('');
    setVoidError('');
    setVoidModalOpen(true);
  };

  const closeVoidModal = () => {
    if (voidSubmitting) return;
    setVoidModalOpen(false);
    setVoidError('');
  };

  const handleVoidLead = async () => {
    if (!leadIdNum) return;
    const reason = voidReasonInput.trim();
    if (!reason) {
      setVoidError('Please enter a reason for voiding this lead.');
      return;
    }
    setVoidSubmitting(true);
    setVoidError('');
    try {
      const updated = await voidClient(leadIdNum, reason);
      setApiLead(updated);
      setVoidModalOpen(false);
      setVoidReasonInput('');
      setLeadMessage('Lead voided.');
      setTimeout(() => setLeadMessage(''), 4000);
    } catch (err) {
      setVoidError(err instanceof Error ? err.message : 'Failed to void lead.');
    } finally {
      setVoidSubmitting(false);
    }
  };

  const openConvertModal = () => {
    setConvertError('');
    setConvertModalOpen(true);
  };

  const closeConvertModal = () => {
    if (convertSubmitting) return;
    setConvertModalOpen(false);
    setConvertError('');
  };

  const handleConvertLead = async () => {
    if (!leadIdNum) return;
    setConvertSubmitting(true);
    setConvertError('');
    try {
      const updated = await convertLeadToClient(leadIdNum);
      setConvertModalOpen(false);
      navigate(`/client/${updated.id}`);
    } catch (err) {
      setConvertError(err instanceof Error ? err.message : 'Failed to convert lead to client.');
    } finally {
      setConvertSubmitting(false);
    }
  };


  if (loading && !leadData) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <div className="page-content">
              <h2 className="page-title">Lead Details</h2>
              <p className="page-subtitle">Loading...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (notFound || !leadData) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <div className="page-content">
              <h2 className="page-title">Lead Not Found</h2>
              <button className="back-button" onClick={() => navigate('/client')}>Back to Clients</button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader
            title={companyName || 'Lead Details'}
            subtitle="View and update lead information"
            onBack={() => navigate('/client')}
            backLabel="Back"
          />
          <div className="page-content">
            {/* Lead info card */}
            <div className="lead-info-card">
              <div className="client-info-header">
                <h3 className="section-title">Lead Information</h3>
                <div className="client-action-buttons">
                  <button
                    type="button"
                    className="client-action-btn client-action-btn--edit"
                    data-tooltip="Edit lead"
                    onClick={handleEditClick}
                    aria-label="Edit lead"
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </button>
                  {!isVoid ? (
                    <button
                      type="button"
                      className="client-action-btn client-action-btn--convert"
                      data-tooltip="Convert to client"
                      onClick={openConvertModal}
                      aria-label="Convert to client"
                    >
                      <FontAwesomeIcon icon={faRightLeft} />
                    </button>
                  ) : null}
                  {!isVoid ? (
                    <button
                      type="button"
                      className="client-action-btn client-action-btn--void"
                      data-tooltip="Void lead"
                      onClick={openVoidModal}
                      aria-label="Void lead"
                    >
                      <FontAwesomeIcon icon={faBan} />
                    </button>
                  ) : null}
                </div>
              </div>

              {isVoid && leadData?.void_reason ? (
                <div className="client-info-void-banner" role="status">
                  <strong>Lead voided.</strong>{' '}
                  <span className="client-info-void-reason-label">Reason:</span>{' '}
                  <span className="client-info-void-reason">{leadData.void_reason}</span>
                </div>
              ) : null}

              <div className="client-info-grid">
                <div className="client-info-item">
                  <label className="client-info-label">Company</label>
                  <div className="client-info-value">{companyName}</div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">Source</label>
                  <div className="client-info-value">{source || '—'}</div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">Employee</label>
                  <div className="client-info-value">{employee || 'Unassigned'}</div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">First Contact</label>
                  <div className="client-info-value">{formatTaskDate(firstContact)}</div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">Product Interested</label>
                  <div className="client-info-value">{productInterested || '—'}</div>
                </div>
              </div>

              <hr className="client-info-divider" />

              <h4 className="client-info-subsection-title">Point of Contact</h4>
              <div className="client-info-grid">
                <div className="client-info-item">
                  <label className="client-info-label">Name</label>
                  <div className="client-info-value">{pointOfContact}</div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">Email</label>
                  <div className="client-info-value">
                    {contactEmail || 'Not set'}
                    <MarketingOptOutBadge client={apiLead} />
                  </div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">Phone</label>
                  <div className="client-info-value">{phoneNumber || 'Not set'}</div>
                </div>
              </div>

              {notes && (
                <>
                  <hr className="client-info-divider" />
                  <h4 className="client-info-subsection-title">Notes</h4>
                  <p className="client-info-value" style={{ whiteSpace: 'pre-wrap' }}>{notes}</p>
                </>
              )}
            </div>

            {leadMessage && (
              <p className="update-message-banner settings-success" role="alert" style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                {leadMessage}
              </p>
            )}


          </div>
        </main>
      </div>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onClose={handleEditCancel} title="Edit Lead Information">
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Company</label>
            <input type="text" className="form-input" value={editCompany} disabled readOnly />
          </div>
          <div className="form-group">
            <label htmlFor="lead-edit-source" className="form-label">Source</label>
            <input
              id="lead-edit-source" type="text" className="form-input"
              value={editSource} onChange={(e) => setEditSource(e.target.value)}
              placeholder="e.g. LinkedIn, Referral"
            />
          </div>
          <div className="form-group">
            <label htmlFor="lead-edit-employee" className="form-label">Assigned Employee</label>
            <SearchableDropdown
              options={verifiedUsers.map((u) => u.username)}
              value={editEmployee}
              onChange={(value) => {
                setEditEmployee(value);
                const u = verifiedUsers.find((x) => x.username === value);
                setEditSelectedUserId(u ? u.id : null);
              }}
              placeholder="Select employee..."
              required={false}
            />
            <p className="form-hint">Who is in charge of reaching out to this lead.</p>
          </div>
          <div className="form-group">
            <label htmlFor="lead-edit-first-contact" className="form-label">First Contact</label>
            <input
              id="lead-edit-first-contact" type="date" className="form-input"
              value={editFirstContact} onChange={(e) => setEditFirstContact(e.target.value)}
            />
            <p className="form-hint">Date of the first outreach. Leave blank if nobody has made contact yet.</p>
          </div>
          <div className="form-group">
            <label htmlFor="lead-edit-product" className="form-label">Product Interested</label>
            <SearchableDropdown
              options={products}
              value={editProduct}
              onChange={setEditProduct}
              placeholder="Select or type product name..."
              required={false}
            />
          </div>

          <hr style={{ margin: '1.25rem 0', borderColor: '#e5e7eb' }} />

          <div className="form-group">
            <label htmlFor="lead-edit-poc" className="form-label">Point of Contact</label>
            <input
              id="lead-edit-poc" type="text" className="form-input"
              value={editPointOfContact} onChange={(e) => setEditPointOfContact(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="lead-edit-email" className="form-label">Email</label>
            <input
              id="lead-edit-email" type="email" className="form-input"
              value={editContactEmail} onChange={(e) => setEditContactEmail(e.target.value)}
              placeholder="contact@company.com"
            />
          </div>
          <div className="form-group">
            <label htmlFor="lead-edit-phone" className="form-label">Phone</label>
            <input
              id="lead-edit-phone" type="tel" className="form-input"
              value={editPhone} onChange={(e) => setEditPhone(e.target.value)}
              placeholder="+1 (555) 000-0000"
            />
          </div>

          <hr style={{ margin: '1.25rem 0', borderColor: '#e5e7eb' }} />

          <div className="form-group">
            <label htmlFor="lead-edit-notes" className="form-label">Notes</label>
            <textarea
              id="lead-edit-notes"
              className="form-textarea"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              rows={4}
              placeholder="Additional notes about this lead..."
            />
          </div>

          {saveError && <p className="create-order-error" role="alert" style={{ marginTop: '0.5rem' }}>{saveError}</p>}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={handleEditCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="update-button" onClick={handleEditSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Modal>

      {/* Void Lead Modal */}
      <Modal isOpen={voidModalOpen} onClose={closeVoidModal} title="Void Lead" narrow>
        <div className="modal-body">
          <p style={{ marginBottom: '1rem' }}>
            Voiding <strong>{companyName}</strong> will mark this lead as inactive.
            Please provide a reason — it will be shown on the Lead Information card.
          </p>
          <div className="form-group">
            <label htmlFor="lead-void-reason" className="form-label">Reason *</label>
            <textarea
              id="lead-void-reason"
              className="form-textarea"
              value={voidReasonInput}
              onChange={(e) => {
                setVoidReasonInput(e.target.value);
                setVoidError('');
              }}
              rows={4}
              placeholder="Why is this lead being voided?"
              disabled={voidSubmitting}
              autoFocus
            />
          </div>
          {voidError ? (
            <p className="create-order-error" role="alert">{voidError}</p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={voidSubmitting} onClick={closeVoidModal}>
            Cancel
          </button>
          <button
            type="button"
            className="client-void-confirm-btn"
            disabled={voidSubmitting || !voidReasonInput.trim()}
            onClick={handleVoidLead}
          >
            {voidSubmitting ? 'Voiding…' : 'Void Lead'}
          </button>
        </div>
      </Modal>

      {/* Convert Lead → Client Modal */}
      <Modal isOpen={convertModalOpen} onClose={closeConvertModal} title="Convert Lead to Client" narrow>
        <div className="modal-body">
          <p style={{ marginBottom: '1rem' }}>
            Convert <strong>{companyName}</strong> from a lead to a client?
            They will be moved to the Clients list and the start date will be set to today (if not already set).
          </p>
          {convertError ? (
            <p className="create-order-error" role="alert">{convertError}</p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={convertSubmitting} onClick={closeConvertModal}>
            Cancel
          </button>
          <button
            type="button"
            className="client-convert-confirm-btn"
            disabled={convertSubmitting}
            onClick={handleConvertLead}
          >
            {convertSubmitting ? 'Converting…' : 'Convert to Client'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default LeadDetail;
